import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, UserIcon, MapPinIcon, LoaderIcon, AlertCircleIcon, StarIcon, PhoneIcon } from 'lucide-react';
interface AllAmericanGutterQuoteFormProps {
  initialZip: string;
  projectType: string;
  onClose: () => void;
}
export function AllAmericanGutterQuoteForm({
  initialZip,
  projectType,
  onClose
}: AllAmericanGutterQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [geoError, setGeoError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const getProductId = (type: string): string => {
    switch (type) {
      case 'gutters':
        return 'Gtr';
      case 'protection':
        return 'GtrPro';
      case 'both':
        return 'Both';
      default:
        return 'Both';
    }
  };
  const getProductDesc = (type: string): string => {
    switch (type) {
      case 'gutters':
        return 'Gutters';
      case 'protection':
        return 'Protection';
      case 'both':
        return 'Both';
      default:
        return 'Both';
    }
  };
  const getProjectLabel = (type: string): string => {
    switch (type) {
      case 'gutters':
        return 'New Gutters';
      case 'protection':
        return 'Gutter Protection';
      case 'both':
        return 'Gutters & Protection';
      default:
        return 'Gutter Project';
    }
  };
  const [formData, setFormData] = useState({
    BDMLeadType: 'test',
    alid: 'AAGP_ALID',
    oid: 'AAGP_OID',
    firstname: '',
    lastname: '',
    phone1: '',
    email: '',
    address1: '',
    city: '',
    state: '',
    zip: initialZip || '',
    srs_id: 'AAGP_SRS',
    sender: 'FindServiceQuotes',
    trustedform_cert: '',
    productid: getProductId(projectType),
    proddescr: getProductDesc(projectType)
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.zip;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      setGeoError('');
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          const stateAbbr = place['state abbreviation'];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: stateAbbr || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.zip]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone1: digits
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            trustedform_cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = 2; // Address, Contact Info (Project Type is already selected)
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const validateAddress = (address: string): boolean => {
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.trustedform_cert;
    const payload = {
      ...formData,
      trustedform_cert: finalCert
    };
    // Submit to BDM lead management
    const bdmParams = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) bdmParams.append(key, String(value));
    });
    const bdmUrl = `http://bdmleadmanagement.valid1.net/Leads.aspx?${bdmParams.toString()}`;
    console.log('Submitting to All American BDM:', bdmUrl);
    try {
      await fetch(bdmUrl, {
        method: 'POST',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('BDM post failed, continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-304">
      <div className="flex flex-col" data-id="element-305">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-306">
          Partner
        </span>
        <div className="flex items-baseline gap-2" data-id="element-307">
          <span className="text-2xl md:text-3xl font-black text-[#4B7B3F] tracking-tight" data-id="element-308">
            All American GP
          </span>
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-600" data-id="element-309">
          {getProjectLabel(projectType)} Estimate
        </span>
      </div>
      <div className="flex items-center gap-6" data-id="element-310">
        <div className="hidden md:flex flex-col items-end" data-id="element-311">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-312">
            Call Us:
          </span>
          <span className="text-lg font-bold text-[#4B7B3F]" data-id="element-313">888-323-8090</span>
        </div>
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#4B7B3F] transition-colors flex items-center gap-1" data-id="element-314">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-315" />
            <span className="hidden sm:inline" data-id="element-316">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-317">
        <Header hideExit data-id="element-318" />

        {/* Hero Banner */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-319">
          <div className="absolute inset-0 z-0" data-id="element-320">
            <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Home improvement professional" className="w-full h-full object-cover" data-id="element-321" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-322">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-[#4B7B3F]/90 flex flex-col justify-center px-8 md:px-16" data-id="element-323">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-324">
                Your Quote Request Has
                <br data-id="element-325" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-326">
                An All American representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-327">
          <div className="flex justify-center mb-6" data-id="element-328">
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center" data-id="element-329">
              <PhoneIcon className="w-10 h-10 text-[#4B7B3F]" data-id="element-330" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-331">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-332">
            An All American representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-333">
              {formData.phone1}
            </span>{' '}
            to discuss your {getProjectLabel(projectType).toLowerCase()} project
            and schedule your free in-home estimate. The call typically comes
            within one business day.
          </p>
        </div>

        {/* Homeowner Info */}
        <div className="max-w-4xl mx-auto w-full px-6 py-12 text-center border-b border-gray-200" data-id="element-334">
          <h2 className="text-[#4B7B3F] font-bold text-sm uppercase tracking-wider mb-6" data-id="element-335">
            Your Submission Details
          </h2>
          <div className="text-gray-900 text-lg mb-2" data-id="element-336">
            <p className="font-semibold text-xl" data-id="element-337">
              {formData.firstname} {formData.lastname}
            </p>
            <p data-id="element-338">{formData.address1}</p>
            <p data-id="element-339">
              {formData.city}, {formData.state} {formData.zip}
            </p>
            <p data-id="element-340">{formData.phone1}</p>
            <p className="text-gray-500" data-id="element-341">{formData.email}</p>
          </div>

          <div className="mt-6 inline-flex flex-col gap-2 text-sm text-gray-600" data-id="element-342">
            <div className="flex items-center justify-center gap-2" data-id="element-343">
              <span className="font-semibold text-gray-800" data-id="element-344">Project Type:</span>
              <span data-id="element-345">{getProjectLabel(projectType)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-[#4B7B3F] py-16 mt-auto" data-id="element-346">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-347">
            <p className="text-white/80 text-sm" data-id="element-348">
              &copy; {new Date().getFullYear()} findservicequotes.io. All Rights
              Reserved.
            </p>
          </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-id="element-349">
      <Header data-id="element-350" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-351" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-352">
        <motion.div className="bg-[#4B7B3F] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-353" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-354">
        <div className="w-full max-w-2xl" data-id="element-355">
          <div className="mb-6 h-8" data-id="element-356">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#4B7B3F] font-medium flex items-center gap-2 transition-colors" data-id="element-357">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-358" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-359">
            <AnimatePresence mode="wait" custom={direction} data-id="element-360">
              {/* STEP 1: Address */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-361">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#4B7B3F] mb-8 text-center" data-id="element-362">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-363">
                    <div data-id="element-364">
                      <label htmlFor="address1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-365">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-366" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-367">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex flex-col gap-2" data-id="element-368">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-369">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-370" />
                            <span data-id="element-371">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-372">
                            <MapPinIcon className="w-4 h-4 text-[#4B7B3F] flex-shrink-0" data-id="element-373" />
                            <input type="text" value={formData.zip} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          zip: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                        setGeoError('');
                      }} onBlur={() => {
                        if (formData.zip.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.zip.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-green-300 rounded-lg text-sm font-semibold text-[#4B7B3F] focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent" data-id="element-374" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className={`flex items-center gap-2 border rounded-lg px-3 py-2 w-full transition-colors cursor-pointer text-left ${geoError ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-green-50 border-green-200 hover:bg-green-100'}`} title="Click to change ZIP code" data-id="element-375">
                            <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${geoError ? 'text-red-500' : 'text-[#4B7B3F]'}`} data-id="element-376" />
                            <span className={`text-sm font-semibold ${geoError ? 'text-red-700' : 'text-[#4B7B3F]'}`} data-id="element-377">
                              {formData.city}, {formData.state} {formData.zip}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-auto" data-id="element-378">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-379">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-380" />
                            <span className="text-sm text-gray-500" data-id="element-381">
                              {formData.zip ? `Zip: ${formData.zip}` : 'Enter your ZIP code'}
                            </span>
                          </button>}

                        {geoError && <div className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-100 mt-2" data-id="element-382">
                            <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-383" />
                            <p className="text-sm font-medium" data-id="element-384">{geoError}</p>
                          </div>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.zip || !!addressError || !!geoError} className="w-full mt-6 h-14 bg-[#4B7B3F] hover:bg-green-800 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-385">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 2: Contact Information */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-386">
                  <div className="flex justify-center mb-6" data-id="element-387">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center" data-id="element-388">
                      <UserIcon className="w-8 h-8 text-[#4B7B3F]" data-id="element-389" />
                    </div>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#4B7B3F] mb-2 text-center" data-id="element-390">
                    Who should we contact?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-391">
                    Your free estimate is almost ready.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-392">
                    <div className="grid grid-cols-2 gap-4" data-id="element-393">
                      <div data-id="element-394">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-395">
                          First Name
                        </label>
                        <input type="text" name="firstname" value={formData.firstname} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent" required data-id="element-396" />
                      </div>
                      <div data-id="element-397">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-398">
                          Last Name
                        </label>
                        <input type="text" name="lastname" value={formData.lastname} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent" required data-id="element-399" />
                      </div>
                    </div>

                    <div data-id="element-400">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-401">
                        Phone Number
                      </label>
                      <input type="tel" name="phone1" value={formData.phone1} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent" placeholder="(555) 555-5555" required maxLength={10} data-id="element-402" />
                    </div>

                    <div data-id="element-403">
                      <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-404">
                        Email Address
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4B7B3F] focus:border-transparent" required data-id="element-405" />
                    </div>

                    <div className="mt-6" data-id="element-406">
                      <label className="flex items-start gap-3 cursor-pointer group" data-id="element-407">
                        <div className="relative flex items-start pt-1" data-id="element-408">
                          <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="peer sr-only" data-id="element-409" />
                          <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#4B7B3F] peer-checked:border-[#4B7B3F] transition-colors flex items-center justify-center" data-id="element-410">
                            <CheckCircle2Icon className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" data-id="element-411" />
                          </div>
                        </div>
                        <span className="text-[11px] leading-tight text-gray-500 select-none" data-id="element-412">
                          By clicking "Get My Free Quote", I agree to the Terms
                          of Service and Privacy Policy. I authorize All
                          American Gutter Protection and its partners to contact
                          me at the phone number and email provided, including
                          via automated technology, text messages, and
                          prerecorded voice. Consent is not a condition of
                          purchase.
                        </span>
                      </label>
                    </div>

                    <button type="submit" disabled={isSubmitting || !formData.firstname || !formData.lastname || formData.phone1.length < 10 || !formData.email || !consentChecked} className="w-full mt-6 h-16 bg-[#FF6F00] hover:bg-orange-700 text-white font-bold text-xl rounded-xl transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2" data-id="element-413">
                      {isSubmitting ? <>
                          <LoaderIcon className="w-6 h-6 animate-spin" data-id="element-414" />
                          Submitting...
                        </> : 'Get My Free Quote'}
                    </button>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}